import React, { useState, useEffect } from 'react';

/**
 * QuantitySelector Component
 * 
 * Production-grade quantity selector with 36px height, square controls,
 * subtle borders, hover states, and accessibility support.
 * 
 * @param {Object} props
 * @param {number} props.quantity - Current quantity value
 * @param {function} props.onQuantityChange - Callback with new quantity value
 * @param {number} [props.min=1] - Minimum allowed quantity
 * @param {number} [props.max=99] - Maximum allowed quantity
 * @param {string} [props.className] - Additional class names
 */
const QuantitySelector = ({ 
  quantity, 
  onQuantityChange, 
  min = 1, 
  max = 99, 
  className = '' 
}) => {
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    if (quantity === min || quantity === max) {
      setPulse(true);
      const timer = setTimeout(() => setPulse(false), 300);
      return () => clearTimeout(timer);
    }
  }, [quantity, min, max]);

  const handleDecrement = () => {
    if (quantity > min) {
      onQuantityChange(quantity - 1);
    }
  };

  const handleIncrement = () => {
    if (quantity < max) {
      onQuantityChange(quantity + 1);
    }
  };

  // Convert quantity to number to safely process input
  const displayQuantity = typeof quantity === 'number' ? quantity : min;

  return (
    <div 
      className={`flex items-center justify-center h-[36px] bg-white dark:bg-brand-surface rounded-input border border-light-border-default dark:border-brand-border-default overflow-hidden ${className}`}
    >
      <button
        type="button"
        onClick={handleDecrement}
        disabled={displayQuantity <= min}
        aria-label="Decrease quantity"
        className="w-[36px] h-[36px] flex items-center justify-center text-light-text-secondary dark:text-brand-text-secondary hover:bg-light-secondary dark:hover:bg-brand-elevated active:scale-95 transition-fast disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 focus:outline-none focus:ring-2 focus:ring-light-accent-red dark:focus:ring-brand-accent-red z-10"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
      </button>

      <div 
        className={`w-[36px] h-[36px] flex items-center justify-center border-x border-light-border-default dark:border-brand-border-default bg-white dark:bg-brand-surface text-light-text-primary dark:text-brand-text-primary font-medium text-body transition-colors duration-300 ${pulse ? 'bg-light-accent-red/10 dark:bg-brand-accent-red/20' : ''}`}
        aria-live="polite"
        aria-atomic="true"
      >
        <span key={displayQuantity} className="animate-scaleIn inline-block">
          {displayQuantity}
        </span>
      </div>

      <button
        type="button"
        onClick={handleIncrement}
        disabled={displayQuantity >= max}
        aria-label="Increase quantity"
        className="w-[36px] h-[36px] flex items-center justify-center text-light-text-secondary dark:text-brand-text-secondary hover:bg-light-secondary dark:hover:bg-brand-elevated active:scale-95 transition-fast disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 focus:outline-none focus:ring-2 focus:ring-light-accent-red dark:focus:ring-brand-accent-red z-10"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
      </button>
    </div>
  );
};

export default QuantitySelector;
